// ─────────────────────────────────────────────────────────────────────────────
// agent_orchestrator.ts — The main agent routing and context engine
//
// Single entry point for all character interactions.  Handles:
//   • Arcane invocation → persona dispatch
//   • Context assembly (page + conversation + NPC + fold state)
//   • LLM call (streaming)
//   • Output parsing → GrimoireAction[]
//   • Notification queue drain
//   • NPC status simulation
//   • Gate hint mechanic (5-hour timer)
//
// This module imports everything else; nothing imports it except the
// integration point in canvas_app / pwa_main.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  PersonaId, InvocationContext, AgentOutput, ChatMessage,
  LlmConfig, NpcSnapshot, NotificationEntry, StreamToken,
  OnStreamToken,
} from '@shared/agent_protocol.js';
import type { FoldState, RoomId } from '@shared/the_fold.js';
import { isNpcPresent, isHouseRoom, shouldOfferGateHint } from '@shared/the_fold.js';
import type { Page, BlockId, BoundingBox } from '@shared/notebook_model.js';
import type { GrimoireAction, SpellDispatchFn } from '@shared/grimoire_action.js';

import { AgentMemory } from './agent_memory.js';
import { streamCompletion, isOnline, LlmError } from './llm_client.js';
import {
  serializePage, extractRegionQuery, getConsumableBlockIds,
} from './page_serializer.js';
import {
  getPersona, getAbsentResponse, buildSystemMessages,
  renderTemplate, type TemplateContext,
} from './persona_loader.js';

// ── Orchestrator ─────────────────────────────────────────────────────────────

export class AgentOrchestrator {
  private memory: AgentMemory;
  private config: LlmConfig | null = null;
  private dispatch: SpellDispatchFn | null = null;
  private activeRequest: AbortController | null = null;

  constructor() {
    this.memory = new AgentMemory();
  }

  async init(config: LlmConfig, dispatch: SpellDispatchFn): Promise<void> {
    this.config = config;
    this.dispatch = dispatch;
    await this.memory.init();
  }

  // ── Configuration ────────────────────────────────────────────────────────

  updateConfig(config: Partial<LlmConfig>): void {
    if (this.config) {
      Object.assign(this.config, config);
    }
  }

  // ── Main invocation entry point ──────────────────────────────────────────

  /**
   * Called when a character's arcane is tapped on a page.
   * This is THE primary interaction pathway.
   *
   * Flow:
   *   1. Check if persona is present in the house
   *   2. Serialize page content (closed vs open region)
   *   3. Build invocation context
   *   4. Assemble system + conversation messages
   *   5. Stream LLM response
   *   6. Parse output → dispatch GrimoireActions
   *   7. Consume arcane + nearby content
   */
  async invoke(
    persona: PersonaId,
    page: Page,
    arcaneBlockId: BlockId,
    regionBounds?: BoundingBox,
    onToken?: OnStreamToken,
    pageEvents?: Array<{ persona: string; kind: string; content: string; timestamp: number }>,
  ): Promise<AgentOutput | null> {
    if (!this.config || !this.dispatch) {
      console.warn('[orchestrator] not initialised');
      return null;
    }

    const fold = await this.memory.getFoldState();

    // ── Presence check ───────────────────────────────────────────────────
    if (!isNpcPresent(persona, fold)) {
      const response = getAbsentResponse(persona);
      // Dispatch a margin annotation with the canned absent response
      this.dispatch({
        type: 'wizard.respond',
        content: response,
        marginalia: {
          pageId: page.id,
          x: 0, y: 0,
          text: response,
          glyphStyle: 'calm',
        },
        origin: 'agent',
        timestamp: Date.now(),
      });
      return null;
    }

    // ── Page serialization ───────────────────────────────────────────────
    const isInside = isHouseRoom(page.id, fold);
    const roomId = fold.rooms.find(r => r.pageId === page.id)?.id;
    const { serialized, regionType } = serializePage(page, {
      regionBounds,
      roomId,
    });
    const query = regionBounds
      ? extractRegionQuery(page, regionBounds)
      : undefined;

    // ── NPC snapshot ─────────────────────────────────────────────────────
    const npc = fold.npcs.find(n => n.persona === persona);

    // ── Conversation history ─────────────────────────────────────────────
    const historyBudget = Math.floor(
      (this.config.contextBudgetTokens ?? 8192) * 0.4,
    );
    const recentMessages = await this.memory.getRecentMessages(
      persona,
      historyBudget,
    );

    // ── Build template context ───────────────────────────────────────────

    // Format cross-NPC events (actions by OTHER personas on this page)
    let otherNpcSummary = '';
    if (pageEvents && pageEvents.length > 0) {
      const otherEvents = pageEvents.filter(e => e.persona !== persona);
      if (otherEvents.length > 0) {
        otherNpcSummary = otherEvents
          .slice(-20)  // last 20 events from other NPCs
          .map(e => {
            const ago = Math.round((Date.now() - e.timestamp) / 1000);
            const agoStr = ago < 60 ? `${ago}s ago` : `${Math.round(ago / 60)}m ago`;
            return `[${e.persona}] (${e.kind}, ${agoStr}): ${e.content}`;
          })
          .join('\n');
      }
    }

    const templateCtx: TemplateContext = {
      persona,
      fold,
      npc,
      pageTitle: page.title,
      roomId,
      activeTimeHours: fold.activeTimeMs / (60 * 60 * 1000),
      recentActionSummary: '[recent actions]',
      pageContentSummary: JSON.stringify(serialized.blocks.slice(0, 20)),
      query,
      isInsideHouse: isInside,
      otherNpcEvents: otherNpcSummary,
    };

    // ── Assemble messages ────────────────────────────────────────────────
    const systemMsgs = buildSystemMessages(templateCtx);
    const userMsg: ChatMessage = {
      role: 'user',
      content: query
        ? `[Invocation with query]\n${query}`
        : `[Invocation — open page context]\nPage: ${page.title}\nContent: ${JSON.stringify(serialized)}`,
      timestamp: Date.now(),
    };

    const allMessages: ChatMessage[] = [
      ...systemMsgs,
      ...recentMessages,
      userMsg,
    ];

    // ── Store user message ───────────────────────────────────────────────
    await this.memory.appendMessage(persona, userMsg);

    // ── Abort any in-flight request ──────────────────────────────────────
    this.activeRequest?.abort();
    this.activeRequest = new AbortController();

    // ── LLM call ─────────────────────────────────────────────────────────
    if (!isOnline()) {
      await this.memory.pushNotification(persona, 'idle', {
        reason: 'offline-invocation',
        pageId: page.id,
      });
      return null;
    }

    try {
      const result = await streamCompletion(
        { messages: allMessages, config: this.config },
        onToken ?? (() => {}),
        this.activeRequest.signal,
      );

      // Store assistant response
      await this.memory.appendMessage(persona, {
        role: 'assistant',
        content: result.content,
        timestamp: Date.now(),
      });

      // Parse output
      const output = this.parseOutput(result.content, persona, page.id);

      // Dispatch actions
      if (output) {
        this.dispatchOutput(output);
      }

      // Consume arcane + nearby content (but NOT persistent NPC channel sigils)
      const arcaneBlock = page.blocks.get(arcaneBlockId);
      const isNpcChannel = arcaneBlock?.kind === 'arcane'
        && (arcaneBlock.arcaneType === 'laura_channel' || arcaneBlock.arcaneType === 'ireth_channel');

      if (!isNpcChannel) {
        const consumeIds = getConsumableBlockIds(
          page,
          arcaneBlockId,
          regionBounds,
        );
        for (const blockId of consumeIds) {
          this.dispatch!({
            type: 'block.delete',
            blockId,
            pageId: page.id,
            origin: 'agent',
            timestamp: Date.now(),
          });
        }
      }

      // Update NPC interaction timestamp
      await this.memory.updateFoldState(s => {
        const npcEntry = s.npcs.find(n => n.persona === persona);
        if (npcEntry) npcEntry.lastInteraction = Date.now();
      });

      return output;
    } catch (err) {
      if (err instanceof LlmError) {
        console.error(`[orchestrator] LLM error: ${err.message}`);
      }
      return null;
    } finally {
      this.activeRequest = null;
    }
  }

  // ── Output parsing ───────────────────────────────────────────────────────

  private parseOutput(
    raw: string,
    persona: PersonaId,
    pageId: string,
  ): AgentOutput | null {
    // Try to extract JSON from the response
    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      // If no JSON found, treat the whole response as ephemeral "say"
      if (raw.trim()) {
        return {
          kind: 'say',
          persona,
          pageId,
          content: raw.trim(),
        };
      }
      return null;
    }

    try {
      const parsed = JSON.parse(jsonMatch[0]);
      // Validate kind
      const validKinds = [
        'say', 'write-text', 'insert-block', 'modify-block', 'displace',
        'write-margin', 'draw-glyph', 'rewrite-page',
        'append-dialog', 'update-npc', 'knock-door', 'update-env', 'no-action',
      ];
      if (!parsed.kind || !validKinds.includes(parsed.kind)) {
        // Default to ephemeral "say" with the full raw content
        console.log(`[orchestrator] parseOutput: invalid kind '${parsed.kind}', defaulting to say`);
        return {
          kind: 'say',
          persona,
          pageId,
          content: raw.trim(),
        };
      }
      console.log(`[orchestrator] parseOutput: kind='${parsed.kind}'`);
      return { ...parsed, persona, pageId } as AgentOutput;
    } catch {
      // JSON parse failed — treat as ephemeral "say"
      return {
        kind: 'say',
        persona,
        pageId,
        content: raw.trim(),
      };
    }
  }

  // ── Dispatch output as GrimoireActions ───────────────────────────────────

  private dispatchOutput(output: AgentOutput): void {
    if (!this.dispatch) return;
    const now = Date.now();

    switch (output.kind) {
      case 'write-text':
        this.dispatch({
          type: 'agent.write',
          agentId: output.persona,
          pageId: output.pageId,
          content: output.content,
          outputKind: 'write-text',
          origin: 'agent',
          timestamp: now,
        });
        break;

      case 'write-margin':
        this.dispatch({
          type: 'wizard.respond',
          content: output.content,
          marginalia: {
            pageId: output.pageId,
            x: 0,
            y: 0,
            text: output.content,
            glyphStyle: output.glyphStyle,
          },
          origin: 'agent',
          timestamp: now,
        });
        break;

      case 'append-dialog':
        this.dispatch({
          type: 'adventure.dialog',
          speaker: output.speaker,
          text: output.text,
          pageId: output.pageId,
          origin: 'agent',
          timestamp: now,
        });
        break;

      case 'update-env':
        this.dispatch({
          type: 'adventure.env',
          env: output.envDelta as Record<string, unknown> & Parameters<typeof Object.assign>[0],
          origin: 'agent',
          timestamp: now,
        });
        break;

      case 'knock-door':
        // Queue a notification for the hall door post board
        this.memory.pushNotification(output.persona, 'npc-autonomous', {
          kind: 'knock',
          message: output.message,
        });
        break;

      case 'say':
        // Ephemeral dialogue — handled directly by the invoker (canvas_app)
        // via the streaming pipeline.  We just archive it in conversation history
        // (already done by addTurn in invoke()).  No persistent GrimoireAction needed.
        break;

      case 'insert-block':
        this.dispatch({
          type: 'agent.write',
          agentId: output.persona,
          pageId: output.pageId,
          content: output.content ?? '',
          outputKind: 'insert-block',
          origin: 'agent',
          timestamp: now,
        });
        break;

      case 'modify-block':
        this.dispatch({
          type: 'agent.modify',
          agentId: output.persona,
          pageId: output.pageId,
          targetBlockId: output.blockId,
          changes: output.delta,
          origin: 'agent',
          timestamp: now,
        });
        break;

      case 'displace':
        this.dispatch({
          type: 'agent.displace',
          agentId: output.persona,
          pageId: output.pageId,
          newPosition: output.position,
          origin: 'agent',
          timestamp: now,
        });
        break;

      case 'no-action':
        // Persona understood but chose not to act — do nothing
        break;

      default:
        // Other output kinds will be handled as we build out renderers
        break;
    }
  }

  // ── Notification drain ─────────────────────────────────────────────────

  /**
   * Process queued notifications for a persona.
   * Called on app open or when visiting a page with the persona's arcane.
   *
   * For hint-timer & first-hint notifications, invokes the LLM with the
   * hintDelivery template to generate an in-character response.
   */
  async drainNotifications(
    persona: PersonaId,
    page?: Page,
  ): Promise<AgentOutput[]> {
    const pending = await this.memory.getPendingNotifications(persona);
    if (pending.length === 0) return [];

    const outputs: AgentOutput[] = [];

    for (const entry of pending) {
      const output = await this._processNotification(entry, persona, page);
      if (output) outputs.push(output);
      await this.memory.markNotificationProcessed(entry.id);
    }

    await this.memory.clearProcessedNotifications();
    return outputs;
  }

  /**
   * Turn a single notification into an LLM-generated agent output.
   */
  private async _processNotification(
    entry: NotificationEntry,
    persona: PersonaId,
    page?: Page,
  ): Promise<AgentOutput | null> {
    if (!this.config || !this.dispatch) return null;
    if (!isOnline()) return null;

    const fold = await this.memory.getFoldState();
    const npc = fold.npcs.find(n => n.persona === persona);
    const personaDef = getPersona(persona);
    const pageId = page?.id ?? 'unknown';

    // Build a minimal template context (no page content for autonomous hints)
    const templateCtx: TemplateContext = {
      persona,
      fold,
      npc,
      pageTitle: page?.title ?? '',
      roomId: fold.rooms.find(r => r.pageId === pageId)?.id,
      activeTimeHours: fold.activeTimeMs / (60 * 60 * 1000),
      recentActionSummary: '',
      pageContentSummary: '',
      query: undefined,
      isInsideHouse: page ? isHouseRoom(page.id, fold) : false,
    };

    // Determine which template to inject based on notification kind
    const kind = entry.payload?.kind as string | undefined;
    let extraSystemContent: string | null = null;

    if (entry.trigger === 'hint-timer' || kind === 'first-hint') {
      // Gate hint: use hintDelivery template if available
      const hintTemplate = personaDef.templates.hintDelivery;
      if (hintTemplate) {
        extraSystemContent = renderTemplate(hintTemplate, templateCtx);
      }
    } else if (kind === 'knock') {
      // NPC knock: the message is already in the payload
      const message = entry.payload?.message as string | undefined;
      if (message) {
        extraSystemContent = `A note has been left on the hall door post: "${message}"`;
      }
    }

    if (!extraSystemContent) {
      // No template for this notification type — skip LLM, just mark processed
      return null;
    }

    // Build messages: system layers + extra notification context
    const systemMsgs = buildSystemMessages(templateCtx);
    systemMsgs.push({
      role: 'system',
      content: extraSystemContent,
      timestamp: Date.now(),
    });

    const userMsg: ChatMessage = {
      role: 'user',
      content: kind === 'first-hint'
        ? '[First autonomous action — leave a trace in the notebook]'
        : `[Notification: ${entry.trigger}]`,
      timestamp: Date.now(),
    };

    const recentMessages = await this.memory.getRecentMessages(persona, 2000);
    const allMessages: ChatMessage[] = [...systemMsgs, ...recentMessages, userMsg];

    await this.memory.appendMessage(persona, userMsg);

    const abort = new AbortController();
    try {
      const result = await streamCompletion(
        { messages: allMessages, config: this.config },
        () => {},  // no streaming animation for autonomous notifications
        abort.signal,
      );

      await this.memory.appendMessage(persona, {
        role: 'assistant',
        content: result.content,
        timestamp: Date.now(),
      });

      const output = this.parseOutput(result.content, persona, pageId);
      if (output) {
        this.dispatchOutput(output);
      }
      return output;
    } catch (err) {
      if (err instanceof LlmError) {
        console.error(`[orchestrator] notification LLM error: ${err.message}`);
      }
      return null;
    }
  }

  // ── Gate hint mechanic ─────────────────────────────────────────────────

  /**
   * Called periodically with updated active time.
   * If the threshold is met, triggers an Ireth hint.
   */
  async checkGateHint(activeTimeMs: number): Promise<boolean> {
    const fold = await this.memory.updateFoldState(s => {
      s.activeTimeMs = activeTimeMs;
    });

    if (shouldOfferGateHint(fold)) {
      await this.memory.pushNotification('ireth', 'hint-timer', {
        hintNumber: fold.gateHintCount + 1,
      });
      await this.memory.updateFoldState(s => {
        s.gateHintCount += 1;
      });
      return true;
    }
    return false;
  }

  // ── Ireth recoherence (first activation) ───────────────────────────────

  /**
   * Called when the user first taps Ireth's arcane on the fence.
   * This initiates the backend — Ireth "recoheres" for the first time.
   * Almost nothing visible happens to the user; this is a backend event.
   */
  async initiateRecoherence(): Promise<void> {
    await this.memory.updateFoldState(s => {
      s.irethReactivated = true;
      const ireth = s.npcs.find(n => n.persona === 'ireth');
      if (ireth) {
        ireth.mood = 'alert';
        ireth.activity = 'recohering — processing reinitiation letter';
      }
    });

    // Queue Ireth's first autonomous action: leave a hint
    await this.memory.pushNotification('ireth', 'npc-autonomous', {
      kind: 'first-hint',
      description: 'Ireth leaves his first hint somewhere in the notebook',
    });
  }

  // ── House binding ──────────────────────────────────────────────────────

  /**
   * Called when the user successfully draws the brand line bookmark
   * (eye-key-lock-eye) at the gate door.
   */
  async bindHouse(): Promise<void> {
    await this.memory.updateFoldState(s => {
      s.bounded = true;
      s.boundAt = Date.now();
      s.era = 'bound';
      const ireth = s.npcs.find(n => n.persona === 'ireth');
      if (ireth) {
        ireth.mood = 'calm';
        ireth.activity = 'settling into the study';
        ireth.roomId = 'study';
      }
    });
  }

  // ── NPC status helpers ─────────────────────────────────────────────────

  async getNpcStatus(persona: PersonaId): Promise<NpcSnapshot | undefined> {
    const fold = await this.memory.getFoldState();
    return fold.npcs.find(n => n.persona === persona);
  }

  async updateNpc(
    persona: PersonaId,
    update: Partial<NpcSnapshot>,
  ): Promise<void> {
    await this.memory.updateFoldState(s => {
      const npc = s.npcs.find(n => n.persona === persona);
      if (npc) Object.assign(npc, update);
    });
  }

  // ── Room unlock ────────────────────────────────────────────────────────

  async unlockRoom(roomId: RoomId, pageId: string): Promise<void> {
    await this.memory.updateFoldState(s => {
      const room = s.rooms.find(r => r.id === roomId);
      if (room) {
        room.unlocked = true;
        room.pageId = pageId;
      }
    });
  }

  // ── Fold state access ──────────────────────────────────────────────────

  async getFoldState(): Promise<FoldState> {
    return this.memory.getFoldState();
  }
}
